import React, { useState, useRef } from 'react';
import { Loader2, Wand2, Save, Printer, Send } from 'lucide-react';
import { ToolLayout } from './ToolLayout';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { OnePagerPreview } from './OnePagerPreview';
import { PreviewScaler } from './PreviewScaler';
import { colorSchemes } from '../data/catalog';
import { printPreview } from '../lib/print';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export function AITool({ title, subtitle, icon, layout, docType, placeholder, examples = [], colorScheme = colorSchemes[0] }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const ref = useRef(null);
  const [prompt, setPrompt] = useState('');
  const [content, setContent] = useState(null);
  const [gen, setGen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const build = () => ({ id: docId, title: content?.title, subtitle: content?.subtitle, content: content || {}, colorScheme, layout, docType });
  const generate = async () => { if (!prompt.trim()) return; setGen(true); try { const r = await api.post('/generate-onepager', { topic: prompt, category: 'General', layout, targetAudience: 'staff' }); setContent(r.data.data); } catch { toast({ title: 'Generation failed', variant: 'destructive' }); } finally { setGen(false); } };
  const save = async () => { if (!user) { toast({ title: 'Sign in to save', variant: 'destructive' }); return; } setSaving(true); try { const p = build(); const r = docId ? await api.put(`/onepagers/${docId}`, p) : await api.post('/onepagers', p); setDocId(r.data.id); toast({ title: 'Saved to library!' }); } catch { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); } };
  return (
    <ToolLayout title={title} subtitle={subtitle} icon={icon}>
      <Card className="mb-4 max-w-3xl"><CardContent className="p-4">
        <Textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={placeholder} className="mb-3 resize-none" />
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={generate} disabled={gen || !prompt.trim()} className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white">{gen ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</> : <><Send className="w-4 h-4 mr-1" /> Generate</>}</Button>
          {!content && examples.map((ex, i) => <button key={i} onClick={() => setPrompt(ex)} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-teal-50 text-gray-600 hover:text-teal-700">{ex}</button>)}
        </div>
      </CardContent></Card>
      {content && (
        <div className="grid lg:grid-cols-[1fr_260px] gap-6">
          <div className="border rounded-xl bg-gray-100 p-4 overflow-hidden"><div ref={ref}><PreviewScaler><OnePagerPreview onePager={build()} /></PreviewScaler></div></div>
          <div className="space-y-3">
            <Button onClick={generate} variant="outline" className="w-full"><Wand2 className="w-4 h-4 mr-1" /> Regenerate</Button>
            <Button onClick={() => printPreview(ref.current)} variant="outline" className="w-full"><Printer className="w-4 h-4 mr-1" /> Print</Button>
            <Button onClick={save} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
          </div>
        </div>
      )}
    </ToolLayout>
  );
}
